// Types shared between the frontend and the Pages Functions backend.

export type MailboxRole = "owner" | "agent" | "read_only";
export type MailboxKind = "personal" | "shared";
export type Tier = "now" | "vendor" | "fyi";
export type Theme = "graphite" | "paper";

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
}

export interface MailboxSummary {
  id: string;
  email: string;
  displayName: string | null;
  kind: MailboxKind;
  role: MailboxRole;
  status: "active" | "reauth_required" | "disconnected";
}

export interface MeResponse {
  user: SessionUser;
  mailboxes: MailboxSummary[];
  prefs: UserPrefs;
}

export interface UserPrefs {
  theme: Theme;
  denseRows: boolean;
  autoAdvance: boolean;
  undoWindowSeconds: 0 | 15 | 30;
  notifDesktop: boolean;
  notifMentions: boolean;
  notifSound: boolean;
  notifMorningDigest: boolean;
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
}

export type Category =
  | "Quote"
  | "Stock check"
  | "RMA"
  | "Tracking"
  | "Price list"
  | "Advisory"
  | "Unsolicited";

export interface ThreadListItem {
  id: string; // gmail thread id
  from: string;
  company: string;
  time: string; // relative, computed client-side ideally, but server sends ISO
  timeIso: string;
  subject: string;
  preview: string;
  tier: Tier;
  category: Category | null;
  assignee: SessionUser | null;
  messageCount: number;
  labelIds: string[];
  unread: boolean;
  hasDraft: boolean;
  hasAttachments: boolean;
  scheduledAt: string | null;
  snoozedUntil: string | null;
  noteCount: number;
}

export interface MessageAttachment {
  filename: string;
  mimeType: string;
  sizeBytes: number;
  attachmentId: string;
  gmailMessageId: string;
}

export interface ThreadMessage {
  gmailMessageId: string;
  who: string;
  email: string;
  to: string;
  timeIso: string;
  mine: boolean;
  bodyText: string;
  bodyHtml: string | null;
  attachments: MessageAttachment[];
}

export interface ReplyDirection {
  label: string;
  blurb: string;
  skill: string | null;
  body: string;
  asksVendor: boolean;
  liveData: boolean;
}

export interface ThreadNote {
  id: string;
  who: SessionUser;
  timeIso: string;
  text: string;
}

export interface ThreadDetail extends ThreadListItem {
  historyId: string;
  messages: ThreadMessage[];
  summary: string | null;
  tasks: string[];
  directions: ReplyDirection[];
  notes: ThreadNote[];
  draftingBy: SessionUser | null;
  waitingSince: string | null;
  archived: boolean;
  spam: boolean;
  deleted: boolean;
  spamReason: string | null;
}

export interface LabelDef {
  id: string;
  name: string;
  color: string;
  parentLabelId: string | null;
  sortOrder: number;
  threadCount: number;
}

export interface ViewDef {
  id: string;
  name: string;
  rule: string | null;
  builtinKey: string | null;
  hidden: boolean;
  sortOrder: number;
  threadCount: number;
}

export interface TeamMember {
  user: SessionUser;
  role: MailboxRole;
}

export const CUSTOM_VIEW_RULES = [
  "assigned_to_me",
  "from_vendors",
  "needs_a_reply",
  "has_a_draft",
  "snoozed_or_waiting",
] as const;
export type CustomViewRule = (typeof CUSTOM_VIEW_RULES)[number];

export interface ImportJobItem {
  key: "mail" | "labels" | "sent" | "signature" | "vacation" | "contacts";
  label: string;
  status: "queued" | "running" | "done" | "error";
  result: string | null;
}

export interface ImportJobStatus {
  id: string;
  status: "queued" | "running" | "done" | "error";
  items: ImportJobItem[];
}
